import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Users, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { useLanguage } from '../context/LanguageContext';
import { getSpendingByMember } from '../lib/api';
import { formatCurrency } from '../lib/utils';

/**
 * Section displaying current-month spending aggregated per family member
 * based on `attributed_member_ids`. Only visible in shared/hybrid modes.
 */
export default function SpendingByMember() {
  const { t, language } = useLanguage();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await getSpendingByMember();
      setData(res.data);
    } catch (e) {
      console.error('Failed to load spending by member', e);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6 flex justify-center py-8">
          <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }
  if (!data || !data.enabled) return null;

  const hasAny = data.members?.some(m => m.total > 0);

  return (
    <Card data-testid="spending-by-member-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          {t('spending_by_member')}
          <span className="ml-auto text-sm text-muted-foreground font-normal">
            {formatCurrency(data.grand_total || 0)}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {!hasAny ? (
          <p className="text-center text-muted-foreground text-sm py-6" data-testid="no-attributed-spending">
            {t('no_attributed_spending')}
          </p>
        ) : (
          <div className="space-y-2">
            {data.members.filter(m => m.total > 0).map((member, idx) => {
              const isOpen = expanded[member.member_id];
              const categories = Object.entries(member.by_category || {})
                .sort(([, a], [, b]) => b - a);
              return (
                <motion.div
                  key={member.member_id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.04 }}
                  className="border rounded-lg overflow-hidden"
                  data-testid={`spending-member-${member.member_id}`}
                >
                  <button
                    type="button"
                    onClick={() => setExpanded(p => ({ ...p, [member.member_id]: !isOpen }))}
                    className="w-full p-3 flex items-center justify-between hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <span className="font-medium">{member.name}</span>
                      <span className="text-xs text-muted-foreground">
                        ({member.transaction_count})
                      </span>
                    </div>
                    <span className="font-semibold text-primary">
                      {formatCurrency(member.total)}
                    </span>
                  </button>
                  {isOpen && categories.length > 0 && (
                    <div className="px-3 pb-3 space-y-1 bg-muted/20">
                      {categories.map(([cat, amount]) => (
                        <div key={cat} className="flex items-center justify-between text-sm py-1">
                          <span className="text-muted-foreground capitalize">{cat}</span>
                          <span>{formatCurrency(amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
